import { useActiveMessagingProvider } from './useActiveMessagingProvider';
import { useEncryptedChat } from './useEncryptedChat';
import { useTwilioConversations } from './useTwilioConversations';
import { createLogger } from '@/lib/logger';

const log = createLogger('chat');

/**
 * Unified chat hook that transparently routes between
 * Elefa E2EE (Signal Protocol) and Twilio Conversations
 * based on the admin-configured active messaging provider.
 *
 * Components consume this hook identically regardless of backend.
 * "Migrate forward only" - old messages remain in their original store.
 */
export function useChat() {
  const { provider, isLoading: providerLoading, isReady: providerReady } = useActiveMessagingProvider();

  const isTwilio = provider === 'TWILIO_CONVERSATIONS';
  const isElefa = !isTwilio; // Default to Elefa

  // Only initialize the active provider's hook - pass enabled flag to prevent
  // unnecessary Supabase subscriptions and encryption operations when inactive
  const elefa = useEncryptedChat({ enabled: isElefa });
  const twilio = useTwilioConversations(isTwilio);

  // While provider config is loading, show loading state
  if (providerLoading || !providerReady) {
    return {
      rooms: [],
      messages: [],
      isLoading: true,
      error: null,
      currentRoom: null,
      sendMessage: async () => {},
      createDirectRoom: async () => null,
      selectRoom: () => {},
      markAsRead: async () => {},
      isEncryptionReady: false,
      initializeEncryption: async () => {},
      hasMoreMessages: false,
      isLoadingMore: false,
      fetchOlderMessages: async () => {},
      activeProvider: provider ?? 'ELEFA',
      providerReady: false,
    };
  }

  if (isTwilio) {
    return {
      rooms: twilio.rooms,
      messages: twilio.messages,
      isLoading: twilio.isLoading,
      error: twilio.error,
      currentRoom: twilio.currentRoom,
      sendMessage: async (...args: Parameters<typeof twilio.sendMessage>) => {
        log.debug('Sending via provider: TWILIO_CONVERSATIONS');
        return twilio.sendMessage(...args);
      },
      createDirectRoom: twilio.createDirectRoom,
      selectRoom: twilio.selectRoom,
      markAsRead: twilio.markAsRead,
      isEncryptionReady: twilio.isReady,
      initializeEncryption: async () => {}, // Twilio handles encryption internally
      hasMoreMessages: twilio.hasMoreMessages,
      isLoadingMore: twilio.isLoadingMore,
      fetchOlderMessages: twilio.fetchOlderMessages,
      activeProvider: 'TWILIO_CONVERSATIONS' as const,
      providerReady: true,
    };
  }

  // Default: Elefa E2EE
  return {
    rooms: elefa.rooms,
    messages: elefa.messages,
    isLoading: elefa.isLoading,
    error: elefa.error,
    currentRoom: elefa.currentRoom,
    sendMessage: async (...args: Parameters<typeof elefa.sendMessage>) => {
      log.debug('Sending via provider: ELEFA');
      return elefa.sendMessage(...args);
    },
    createDirectRoom: elefa.createDirectRoom,
    selectRoom: elefa.selectRoom,
    markAsRead: elefa.markAsRead,
    isEncryptionReady: elefa.isEncryptionReady,
    initializeEncryption: elefa.initializeEncryption,
    hasMoreMessages: elefa.hasMoreMessages,
    isLoadingMore: elefa.isLoadingMore,
    fetchOlderMessages: elefa.fetchOlderMessages,
    activeProvider: 'ELEFA' as const,
    providerReady: true,
    needsRecovery: elefa.needsRecovery,
    setNeedsRecovery: elefa.setNeedsRecovery,
  };
}
